import { useEffect, useState } from 'react';
import { useOrganizationBranding } from '@/hooks/useOrganizationBranding';
import { Loader2 } from 'lucide-react';

interface LoadingProps {
  onComplete: () => void;
}

const Loading = ({ onComplete }: LoadingProps) => {
  const [showLogo, setShowLogo] = useState(false);
  const { organizationName, organizationLogo, logoAltText, organizationInitial, isLoading } = useOrganizationBranding();

  useEffect(() => {
    // Always show loading animation for at least 1 second
    const timer = setTimeout(() => {
      setShowLogo(true);
      
      // Complete loading after animation
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 1000);
      
      return () => clearTimeout(completeTimer);
    }, 500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20">
      <div className="flex flex-col items-center space-y-6">
        {/* Organization logo with fade-in animation */}
        <div className={`transition-all duration-1000 ${showLogo ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <img 
            src={organizationLogo} 
            alt={logoAltText}
            className="h-24 w-24 object-contain"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src !== '/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png') {
                target.src = '/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png';
              }
            }}
          />
        </div>
        
        {/* Organization name */}
        <div className={`text-center transition-all duration-1000 delay-300 ${showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h1 className="text-3xl font-bold text-foreground">
            {organizationName}
          </h1>
          <p className="text-muted-foreground mt-2">Welcome to our platform</p>
        </div>

        {/* Loading indicator */}
        <div className={`transition-all duration-1000 delay-500 ${showLogo ? 'opacity-100' : 'opacity-0'}`}>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    </div>
  );
};

export default Loading;